import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts';
import { TrendingUp, TrendingDown, Users, Package, Target, Banknote, Smile, PartyPopper, History, CalendarOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';
import { TopOptionCard } from '../components/TopOptionCard';
import { OptionAlertsCard } from '../components/OptionAlertsCard';
import { cheerMessage } from '../utils/gender';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = [
  'hsl(221, 83%, 53%)',  // vivid blue
  'hsl(0, 84%, 60%)',    // red
  'hsl(142, 76%, 36%)',  // green
  'hsl(280, 70%, 55%)',  // purple
  'hsl(25, 95%, 55%)',   // orange
  'hsl(190, 85%, 45%)',  // teal
  'hsl(330, 75%, 55%)',  // pink
  'hsl(48, 96%, 53%)',   // yellow
  'hsl(165, 70%, 40%)',  // emerald
  'hsl(255, 70%, 60%)',  // indigo
  'hsl(15, 80%, 50%)',   // brick
  'hsl(200, 80%, 40%)',  // sky-blue
  'hsl(95, 60%, 45%)',   // lime
  'hsl(310, 65%, 50%)',  // magenta
  'hsl(50, 90%, 45%)',   // gold
];

const RADIAN = Math.PI / 180;
const renderDonutLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  if (!percent || percent < 0.03) return null;
  const r = outerRadius + 18;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  const isRight = Math.cos(-midAngle * RADIAN) >= 0;
  const text = `${(percent * 100).toFixed(0)}%`;
  // Approximate width based on character count for the white background pill
  const w = text.length * 8 + 6;
  const px = isRight ? x : x - w;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <rect x={px} y={y - 9} width={w} height={18} rx={9} ry={9} fill="rgba(255,255,255,0.95)" stroke="hsl(214, 32%, 91%)" strokeWidth={0.5} />
      <text
        x={x}
        y={y}
        fill="hsl(222, 47%, 11%)"
        textAnchor={isRight ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={11}
        fontWeight={700}
        dx={isRight ? 3 : -3}
      >
        {text}
      </text>
    </g>
  );
};

export default function Dashboard() {
  const { user, employee, branch } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/dashboard`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-muted-foreground">جاري التحميل...</div>
      </div>
    );
  }

  return <UnifiedDashboard analytics={analytics} user={user} branch={branch} />;
}

// ─── AutoRefreshBadge ───────────────────────────────────────────────
// Live countdown chip shown next to the "لمحة سريعة" title on LARGE
// screens only (≥1536px). It mirrors the global auto-refresh interval
// handled by Layout.js (10 minutes) so TV viewers see exactly how long
// until the next full-page reload.
const AUTO_REFRESH_INTERVAL_SEC = 600;
const AutoRefreshBadge = React.memo(function AutoRefreshBadge({ welcome = '' }) {
  const [enabled, setEnabled] = React.useState(() => {
    try { return localStorage.getItem('qimam:auto-refresh-enabled') === '1'; } catch { return false; }
  });
  const [mountedAt] = React.useState(() => Date.now());
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!enabled) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [enabled]);

  React.useEffect(() => {
    const sync = () => {
      try { setEnabled(localStorage.getItem('qimam:auto-refresh-enabled') === '1'); } catch {}
    };
    window.addEventListener('auto-refresh:changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('auto-refresh:changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (!enabled) {
    // Auto-refresh OFF: still show the welcome/tagline on large screens next
    // to the title area (otherwise they'd disappear since the 2xl flow moves
    // them inside the badge).
    if (!welcome) return null;
    return (
      <div
        className="hidden 2xl:flex flex-col leading-tight"
        data-testid="dashboard-welcome-inline"
      >
        <span className="text-2xl font-bold text-muted-foreground">ياهلا {welcome}</span>
        <span className="text-lg text-primary/80 font-bold mt-1">رحلة قِمَّتك تبدأ الآن</span>
      </div>
    );
  }

  const elapsedSec = Math.floor((now - mountedAt) / 1000);
  const remaining = Math.max(0, AUTO_REFRESH_INTERVAL_SEC - elapsedSec);
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const refreshing = remaining <= 0;

  return (
    <div
      className="hidden 2xl:inline-flex items-center gap-5 px-7 py-5 rounded-2xl bg-brand_blue/10 border-2 border-brand_blue/30 text-brand_blue shadow-sm"
      data-testid="dashboard-refresh-badge"
    >
      <RefreshCw className={`h-12 w-12 ${refreshing ? 'animate-spin' : ''}`} />
      <div className="flex flex-col leading-tight">
        <span className="text-base font-bold text-brand_blue/70 uppercase tracking-wider">التحديث التلقائي</span>
        <span className="text-5xl font-black font-numbers" dir="ltr">
          {refreshing ? 'جارٍ التحديث…' : `${mm}:${String(ss).padStart(2, '0')}`}
        </span>
      </div>
      {welcome && (
        <div
          className="flex flex-col leading-tight ps-5 ms-2 border-s border-brand_blue/25 text-brand_blue/90"
          data-testid="dashboard-welcome-inline"
        >
          <span className="text-2xl font-bold">ياهلا {welcome}</span>
          <span className="text-lg font-bold mt-1">رحلة قِمَّتك تبدأ الآن</span>
        </div>
      )}
    </div>
  );
});

function UnifiedDashboard({ analytics, user, branch }) {
  const isEmployee = user?.role === 'employee';

  // Track large-screen breakpoint (>=1536px / Tailwind 2xl) so the donut chart
  // scales up on 65-inch TVs. Recharts uses numeric props so we can't rely on
  // Tailwind's `2xl:` prefix here.
  const [isLargeScreen, setIsLargeScreen] = React.useState(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1536px)').matches
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1536px)');
    const handler = (e) => setIsLargeScreen(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const productSalesData = Object.values(analytics?.product_sales || {})
    .map(product => ({
      name: product.name,
      value: product.total
    }))
    // Sort by achieved value descending so the top-selling product (e.g. صندوق بريد)
    // leads the legend and its slice occupies the first color in the palette.
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  const dailyPercentage = analytics?.daily_percentage || 0;
  const weeklyPercentage = analytics?.weekly_percentage || 0;
  const monthlyPercentage = analytics?.monthly_percentage || 0;
  const quarterlyPercentage = analytics?.quarterly_percentage || 0;
  const yesterdayPercentage = analytics?.yesterday_percentage || 0;
  const todaySales = analytics?.daily_sales || 0;
  const yesterdaySales = analytics?.yesterday_sales || 0;
  const dayDelta = todaySales - yesterdaySales;
  const dayDeltaPct = yesterdaySales > 0 ? (dayDelta / yesterdaySales) * 100 : (todaySales > 0 ? 100 : 0);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-3xl sm:text-4xl font-black text-primary tracking-tight">
            لمحة سريعة
          </h1>
          {/* On large (≥1536px) screens the welcome + tagline sit INSIDE the
              countdown badge area so they stay readable on the TV. On smaller
              screens they fall back below the title (see the <p>s below). */}
          <AutoRefreshBadge welcome={user?.name || ''} />
        </div>
        <p className="text-muted-foreground mt-2 2xl:hidden" data-testid="dashboard-welcome">ياهلا {user?.name || ''}</p>
        <p className="text-sm text-primary/80 font-bold mt-1 2xl:hidden" data-testid="dashboard-tagline">رحلة قِمَّتك تبدأ الآن</p>
      </div>

      {/* Employee Personal Stats - Only for Employees */}
      {user?.role === 'employee' && analytics?.my_employee_data && (
        <EmployeeWeeklyHero data={analytics.my_employee_data} user={user} />
      )}

      <TopOptionCard mine={isEmployee} />
      {!isEmployee && <OptionAlertsCard />}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        {!isEmployee && (<>
        <Card className="p-6 border-border shadow-sm hover:shadow-md transition-all" data-testid="daily-sales-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm 2xl:text-2xl font-bold text-muted-foreground uppercase tracking-wider">مبيعات اليوم</p>
              <p className="text-2xl sm:text-3xl 2xl:text-4xl font-black text-primary mt-2 font-numbers">
                {Math.round(analytics?.daily_sales || 0).toLocaleString('en-US')} <SAR />
              </p>
            </div>
            <div className="h-12 w-12 2xl:h-20 2xl:w-20 rounded-lg bg-warning/10 flex items-center justify-center">
              <Banknote className="h-6 w-6 2xl:h-10 2xl:w-10 text-warning" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 bg-secondary rounded-full h-2 2xl:h-3">
              <div
                className="bg-warning h-2 2xl:h-3 rounded-full transition-all"
                style={{ width: `${Math.min(dailyPercentage, 100)}%` }}
              />
            </div>
            <span className="text-sm 2xl:text-xl font-bold text-warning font-numbers">
              {dailyPercentage.toFixed(0)}%
            </span>
          </div>
          <p className="text-xs 2xl:text-lg text-muted-foreground mt-2">
            الهدف: {analytics?.branch_daily_target?.toLocaleString('en-US') || 0} <SAR />
          </p>
        </Card>

        <Card className="p-6 border-border shadow-sm hover:shadow-md transition-all bg-gradient-to-br from-slate-50 to-white" data-testid="yesterday-sales-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm 2xl:text-2xl font-bold text-muted-foreground uppercase tracking-wider">مبيعات أمس</p>
              <p className="text-2xl sm:text-3xl 2xl:text-4xl font-black text-slate-700 mt-2 font-numbers">
                {Math.round(yesterdaySales).toLocaleString('en-US')} <SAR />
              </p>
            </div>
            <div className="h-12 w-12 2xl:h-20 2xl:w-20 rounded-lg bg-slate-200 flex items-center justify-center">
              <History className="h-6 w-6 2xl:h-10 2xl:w-10 text-slate-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 bg-secondary rounded-full h-2 2xl:h-3">
              <div
                className="bg-slate-500 h-2 2xl:h-3 rounded-full transition-all"
                style={{ width: `${Math.min(yesterdayPercentage, 100)}%` }}
              />
            </div>
            <span className="text-sm 2xl:text-xl font-bold text-slate-600 font-numbers">
              {yesterdayPercentage.toFixed(0)}%
            </span>
          </div>
          <p className="text-xs 2xl:text-lg text-muted-foreground mt-2 flex items-center justify-between">
            <span>الهدف: {(analytics?.yesterday_target || 0).toLocaleString('en-US')} <SAR /></span>
            {(yesterdaySales > 0 || todaySales > 0) && (
              <span
                className={`flex items-center gap-1 font-bold font-numbers ${dayDelta >= 0 ? 'text-success' : 'text-destructive'}`}
                data-testid="day-delta-indicator"
                title="مقارنة اليوم بأمس"
              >
                {dayDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {dayDelta >= 0 ? '+' : ''}{dayDeltaPct.toFixed(0)}%
              </span>
            )}
          </p>
        </Card>

        <Card className="p-6 border-border shadow-sm hover:shadow-md transition-all" data-testid="weekly-sales-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm 2xl:text-2xl font-bold text-muted-foreground uppercase tracking-wider">مبيعات الأسبوع</p>
              <p className="text-2xl sm:text-3xl 2xl:text-4xl font-black text-primary mt-2 font-numbers">
                {Math.round(analytics?.weekly_sales || 0).toLocaleString('en-US')} <SAR />
              </p>
            </div>
            <div className="h-12 w-12 2xl:h-20 2xl:w-20 rounded-lg bg-success/10 flex items-center justify-center">
              <Banknote className="h-6 w-6 2xl:h-10 2xl:w-10 text-success" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 bg-secondary rounded-full h-2 2xl:h-3">
              <div
                className="bg-success h-2 2xl:h-3 rounded-full transition-all"
                style={{ width: `${Math.min(weeklyPercentage, 100)}%` }}
              />
            </div>
            <span className="text-sm 2xl:text-xl font-bold text-success font-numbers">
              {weeklyPercentage.toFixed(0)}%
            </span>
          </div>
          <p className="text-xs 2xl:text-lg text-muted-foreground mt-2">
            الهدف: {analytics?.branch_weekly_target?.toLocaleString('en-US') || 0} <SAR />
          </p>
        </Card>

        <Card className="p-6 border-border shadow-sm hover:shadow-md transition-all" data-testid="monthly-sales-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm 2xl:text-2xl font-bold text-muted-foreground uppercase tracking-wider">مبيعات الشهر</p>
              <p className="text-2xl sm:text-3xl 2xl:text-4xl font-black text-primary mt-2 font-numbers">
                {Math.round(analytics?.monthly_sales || 0).toLocaleString('en-US')} <SAR />
              </p>
            </div>
            <div className="h-12 w-12 2xl:h-20 2xl:w-20 rounded-lg bg-brand_blue/10 flex items-center justify-center">
              <Banknote className="h-6 w-6 2xl:h-10 2xl:w-10 text-brand_blue" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 bg-secondary rounded-full h-2 2xl:h-3">
              <div
                className="bg-brand_blue h-2 2xl:h-3 rounded-full transition-all"
                style={{ width: `${Math.min(monthlyPercentage, 100)}%` }}
              />
            </div>
            <span className="text-sm 2xl:text-xl font-bold text-brand_blue font-numbers">
              {monthlyPercentage.toFixed(0)}%
            </span>
          </div>
          <p className="text-xs 2xl:text-lg text-muted-foreground mt-2">
            الهدف: {analytics?.branch_monthly_target?.toLocaleString('en-US') || 0} <SAR />
          </p>
        </Card>

        <Card className="p-6 border-border shadow-sm hover:shadow-md transition-all" data-testid="quarterly-sales-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm 2xl:text-2xl font-bold text-muted-foreground uppercase tracking-wider">مبيعات الربع</p>
              <p className="text-2xl sm:text-3xl 2xl:text-4xl font-black text-primary mt-2 font-numbers">
                {Math.round(analytics?.quarterly_sales || 0).toLocaleString('en-US')} <SAR />
              </p>
            </div>
            <div className="h-12 w-12 2xl:h-20 2xl:w-20 rounded-lg bg-warning/10 flex items-center justify-center">
              <Banknote className="h-6 w-6 2xl:h-10 2xl:w-10 text-warning" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 bg-secondary rounded-full h-2 2xl:h-3">
              <div
                className="bg-warning h-2 2xl:h-3 rounded-full transition-all"
                style={{ width: `${Math.min(quarterlyPercentage, 100)}%` }}
              />
            </div>
            <span className="text-sm 2xl:text-xl font-bold text-warning font-numbers">
              {quarterlyPercentage.toFixed(0)}%
            </span>
          </div>
          <p className="text-xs 2xl:text-lg text-muted-foreground mt-2">
            الهدف: {analytics?.branch_quarterly_target?.toLocaleString('en-US') || 0} <SAR />
          </p>
        </Card>

        <Card className="p-6 border-border shadow-sm hover:shadow-md transition-all" data-testid="transactions-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm 2xl:text-2xl font-bold text-muted-foreground uppercase tracking-wider">عدد المعاملات</p>
              <p className="text-2xl sm:text-3xl 2xl:text-4xl font-black text-primary mt-2 font-numbers">
                {analytics?.total_transactions || 0}
              </p>
            </div>
            <div className="h-12 w-12 2xl:h-20 2xl:w-20 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 2xl:h-10 2xl:w-10 text-primary" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs 2xl:text-lg">
            <div>
              <p className="text-muted-foreground">موظفين</p>
              <p className="font-bold font-numbers 2xl:text-2xl">{analytics?.total_employees || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground">منتجات</p>
              <p className="font-bold font-numbers 2xl:text-2xl">{analytics?.total_products || 0}</p>
            </div>
          </div>
        </Card>
        </>)}
      </div>

      {!isEmployee && (
      <>
      {/* Charts */}
      <div className="grid grid-cols-1 gap-6">
        <Card className="p-6 border-border shadow-sm" data-testid="product-sales-chart">
          <h3 className="text-lg sm:text-xl 2xl:text-3xl font-bold text-primary mb-6">مبيعات المنتجات (هذا الأسبوع)</h3>
          {isLargeScreen ? (
            // ── Large screens (65-inch TVs, ≥1536px): vertical BAR chart so every
            //     product name is fully visible along the X-axis. Extra bottom
            //     margin (170) reserves room for the tilted product names and
            //     lifts the plot area so no label is clipped. Values + % appear
            //     above every bar via <LabelList>.
            (() => {
              const barTotal = productSalesData.reduce((s, d) => s + (Number(d.value) || 0), 0);
              return (
                <ResponsiveContainer width="100%" height={700}>
                  <BarChart
                    data={productSalesData}
                    margin={{ top: 60, right: 30, bottom: 170, left: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                    <XAxis
                      dataKey="name"
                      interval={0}
                      angle={-55}
                      textAnchor="end"
                      tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 13, fontWeight: 700 }}
                      height={170}
                    />
                    <YAxis
                      tick={{ fill: 'hsl(222, 47%, 30%)', fontSize: 16 }}
                      tickFormatter={(v) => Number(v).toLocaleString('en-US')}
                    />
                    <Tooltip
                      formatter={(value) => [`${Number(value).toLocaleString('en-US')} ر.س`, 'المبيعات']}
                      contentStyle={{
                        backgroundColor: 'hsl(0, 0%, 100%)',
                        border: '1px solid hsl(214, 32%, 91%)',
                        borderRadius: '8px',
                        direction: 'rtl',
                        fontFamily: 'inherit',
                        fontSize: '18px',
                      }}
                      cursor={{ fill: 'hsl(214, 32%, 91%)', opacity: 0.35 }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                      {productSalesData.map((entry, index) => (
                        <Cell key={`bar-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                      {/* Two stacked labels above every bar. Kept ~20px apart
                          vertically to avoid overlap when the bar is short. */}
                      <LabelList
                        dataKey="value"
                        content={(props) => {
                          const { x, y, width, value } = props;
                          if (value == null) return null;
                          const pct = barTotal > 0 ? (Number(value) / barTotal) * 100 : 0;
                          const cx = Number(x) + Number(width) / 2;
                          return (
                            <text
                              x={cx}
                              y={Number(y) - 32}
                              textAnchor="middle"
                              fill="hsl(222, 47%, 11%)"
                              fontSize={17}
                              fontWeight={800}
                            >
                              {pct.toFixed(1)}%
                            </text>
                          );
                        }}
                      />
                      <LabelList
                        dataKey="value"
                        content={(props) => {
                          const { x, y, width, value } = props;
                          if (value == null) return null;
                          const cx = Number(x) + Number(width) / 2;
                          return (
                            <text
                              x={cx}
                              y={Number(y) - 12}
                              textAnchor="middle"
                              fill="hsl(222, 47%, 30%)"
                              fontSize={15}
                              fontWeight={600}
                            >
                              {Number(value).toLocaleString('en-US')}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()
          ) : (
            // ── Mobile / desktop <1536px: original donut view is unchanged.
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 16, right: 36, bottom: 8, left: 36 }}>
                  <Pie
                    data={productSalesData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderDonutLabel}
                    outerRadius={75}
                    innerRadius={40}
                    paddingAngle={2}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {productSalesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${Number(value).toLocaleString('en-US')} ر.س`, name]}
                    contentStyle={{
                      backgroundColor: 'hsl(0, 0%, 100%)',
                      border: '1px solid hsl(214, 32%, 91%)',
                      borderRadius: '8px',
                      direction: 'rtl',
                      fontFamily: 'inherit',
                      fontSize: '13px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div
                dir="rtl"
                className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm"
                data-testid="product-legend"
              >
                {productSalesData.map((entry, index) => (
                  <div key={`legend-${index}`} className="flex items-center gap-2">
                    <span
                      className="inline-block rounded-full h-3 w-3 flex-shrink-0"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-foreground font-bold">{entry.name}</span>
                    <span className="text-muted-foreground font-numbers">
                      {Number(entry.value || 0).toLocaleString('en-US')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Employee Performance Table */}
      <Card className="p-6 border-border shadow-sm">
        <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">أداء الموظفين لهذا الأسبوع</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-right py-3 px-4 text-sm font-bold text-foreground">الموظف</th>
                <th className="text-right py-3 px-4 text-sm font-bold text-foreground">الهدف</th>
                <th className="text-right py-3 px-4 text-sm font-bold text-foreground">المحقق</th>
                <th className="text-right py-3 px-4 text-sm font-bold text-foreground">المتبقي</th>
                <th className="text-right py-3 px-4 text-sm font-bold text-foreground">النسبة</th>
                <th className="text-right py-3 px-4 text-sm font-bold text-foreground">الحالة</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.employee_sales?.map((emp) => (
                <tr key={emp.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                  <td className="py-3 px-4 text-sm text-foreground">{emp.name}</td>
                  <td className="py-3 px-4 text-sm text-foreground font-numbers">
                    {emp.weekly_target.toLocaleString('en-US')} <SAR />
                  </td>
                  <td className="py-3 px-4 text-sm text-success font-numbers font-bold">
                    {emp.weekly_total.toLocaleString('en-US')} <SAR />
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground font-numbers">
                    {emp.weekly_remaining.toLocaleString('en-US')} <SAR />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-secondary rounded-full h-2 max-w-[100px]">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            emp.weekly_percentage >= 100 ? 'bg-success' : 'bg-brand_blue'
                          }`}
                          style={{ width: `${Math.min(emp.weekly_percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold font-numbers">
                        {emp.weekly_percentage.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4" data-testid={`dash-attendance-${emp.id}`}>
                    {emp.attendance === 'on_leave' ? (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 font-bold text-xs w-fit">
                          <CalendarOff className="h-3 w-3" />
                          إجازة
                        </span>
                        {emp.leave_period && (
                          <span className="text-[10px] text-muted-foreground font-numbers" dir="ltr">
                            {emp.leave_period.start} → {emp.leave_period.end}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-bold text-xs">
                        نشط
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}
    </div>
  );
}

function EmployeeWeeklyHero({ data, user }) {
  const target = data.weekly_target || 0;
  const achieved = data.weekly_total || 0;
  const remaining = Math.max(target - achieved, 0);
  const pct = target > 0 ? (achieved / target) * 100 : 0;
  const reached = target > 0 && achieved >= target;
  const over = target > 0 && achieved > target;

  return (
    <Card
      data-testid="employee-personal-card"
      className={`p-6 md:p-8 border-2 transition-all ${
        over ? 'border-success bg-gradient-to-br from-success/10 to-success/5'
          : reached ? 'border-success/70 bg-gradient-to-br from-success/8 to-success/4'
          : 'border-brand_blue bg-gradient-to-br from-brand_blue/5 to-success/5'
      }`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center">
          <Target className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-black text-primary">أدائي الشخصي</h2>
          <p className="text-sm text-foreground/70">المستهدف الأسبوعي والإنجاز</p>
        </div>
        <div className="ms-auto">
          <span className={`text-3xl md:text-4xl font-black font-numbers ${reached ? 'text-success' : 'text-primary'}`}>
            {pct.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* 3-up: target, achieved, remaining (remaining is BIG and RED) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="text-center p-4 rounded-xl bg-primary/5">
          <p className="text-xs font-bold text-muted-foreground mb-2">الهدف الأسبوعي</p>
          <p
            data-testid="weekly-target-value"
            className="text-2xl md:text-3xl font-black text-primary font-numbers flex items-center justify-center gap-1"
          >
            {target.toLocaleString('en-US')}
            <SAR size="0.85em" />
          </p>
        </div>

        <div className="text-center p-4 rounded-xl bg-success/10">
          <p className="text-xs font-bold text-muted-foreground mb-2">المحقق</p>
          <p
            data-testid="weekly-achieved-value"
            className="text-2xl md:text-3xl font-black text-success font-numbers flex items-center justify-center gap-1"
          >
            {achieved.toLocaleString('en-US')}
            <SAR size="0.85em" />
          </p>
        </div>

        <div className={`text-center p-4 rounded-xl ${remaining > 0 ? 'bg-destructive/10 border-2 border-destructive/30' : 'bg-success/10'}`}>
          <p className="text-xs font-bold text-muted-foreground mb-2">المتبقي</p>
          <p
            data-testid="weekly-remaining-value"
            className={`text-3xl md:text-5xl font-black font-numbers flex items-center justify-center gap-1 ${
              remaining > 0 ? 'text-destructive' : 'text-success'
            }`}
          >
            {remaining.toLocaleString('en-US')}
            <SAR size="0.9em" />
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 bg-secondary rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${reached ? 'bg-success' : 'bg-brand_blue'}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      </div>

      {/* State badge */}
      {over && (
        <div
          data-testid="personal-state-badge"
          data-state="over"
          className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl bg-gradient-to-l from-success/25 to-success/10 border-2 border-success text-success"
        >
          <PartyPopper className="h-7 w-7 animate-pulse" />
          <span className="text-2xl md:text-3xl font-black">{cheerMessage(user?.name, true)}</span>
          <span className="text-3xl">💪</span>
        </div>
      )}
      {!over && reached && (
        <div
          data-testid="personal-state-badge"
          data-state="done"
          className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl bg-success/15 border-2 border-success text-success"
        >
          <span className="text-3xl md:text-4xl font-black">{cheerMessage(user?.name, false)}</span>
          <Smile className="h-9 w-9 md:h-10 md:w-10" strokeWidth={2.5} />
        </div>
      )}

      {/* Daily / Monthly mini-stats */}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="bg-white/50 rounded-lg p-3">
          <p className="text-xs font-bold text-muted-foreground">اليوم</p>
          <p className="text-lg font-black text-warning font-numbers mt-1 flex items-center gap-1">
            {data.daily_total?.toLocaleString('en-US') || 0} <SAR size="0.85em" />
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            من أصل {data.daily_target?.toLocaleString('en-US') || 0} ({(data.daily_percentage || 0).toFixed(0)}%)
          </p>
        </div>
        <div className="bg-white/50 rounded-lg p-3">
          <p className="text-xs font-bold text-muted-foreground">الشهر</p>
          <p className="text-lg font-black text-success font-numbers mt-1 flex items-center gap-1">
            {data.monthly_total?.toLocaleString('en-US') || 0} <SAR size="0.85em" />
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            من أصل {data.monthly_target?.toLocaleString('en-US') || 0} ({(data.monthly_percentage || 0).toFixed(0)}%)
          </p>
        </div>
      </div>
    </Card>
  );
}


